import React, { FC, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { ApexOptions } from 'apexcharts'
import { KTIcon } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { ToastContainer } from 'react-toastify'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import DataTable from 'react-data-table-component'
import Select from 'react-select'

import KpiCard from './components/KpiCard'
import ChartCard from './components/ChartCard'
import { selectStyles } from './components/selectStyles'
import { useBookingAnalytics, useCategoryList, ALL_CATEGORY_OPTION, type BookingSummary, type TopCustomer } from './useBookingAnalytics'

const MAX_LISTING_BARS = 10
const MAX_CANCELLATION_ITEMS = 6

// ─── Filter Bar ───
const FilterBar = React.memo<{
  dateRange: Date[]
  selectedCategory: any
  categoryList: any[]
  onDateChange: (dates: Date[]) => void
  onCategoryChange: (val: any) => void
  onClear: () => void
  t: (key: string) => string
}>(({ dateRange, selectedCategory, categoryList, onDateChange, onCategoryChange, onClear, t }) => {
  const hasFilters = (dateRange?.length ?? 0) > 0 || (selectedCategory?.value && selectedCategory.value !== '')
  const flatpickrRef = useRef<any>(null)

  const clearDate = useCallback(() => {
    if (flatpickrRef.current?.flatpickr) flatpickrRef.current.flatpickr.clear()
    onDateChange([])
  }, [onDateChange])

  return (
    <div className="card border-0 shadow-sm mb-6">
      <div className="card-body py-5 px-6">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center position-relative">
            <KTIcon iconName="calendar" className="fs-3 position-absolute ms-4 text-gray-500" />
            <Flatpickr
              ref={flatpickrRef}
              className="form-control form-control-solid w-275px ps-12 fs-7"
              placeholder={t('Select date range')}
              value={dateRange}
              onChange={(dates: Date[]) => { if (dates?.length === 2) onDateChange(dates) }}
              options={{ mode: 'range' as const, dateFormat: 'd M Y', monthSelectorType: 'static' as const }}
            />
          </div>
          {(dateRange?.length ?? 0) > 0 ? (
            <button className="btn btn-icon btn-sm btn-light-danger" onClick={clearDate} title={t('Clear date')}>
              <KTIcon iconName="cross" className="fs-5" />
            </button>
          ) : null}
          <div style={{ minWidth: 220 }}>
            <Select
              options={categoryList}
              value={selectedCategory}
              onChange={(val: any) => onCategoryChange(val ?? ALL_CATEGORY_OPTION)}
              isClearable={selectedCategory?.value !== ''}
              placeholder={t('All')}
              styles={selectStyles}
            />
          </div>
          {hasFilters ? (
            <button className="btn btn-sm btn-light-danger fs-7" onClick={onClear}>
              <KTIcon iconName="cross" className="fs-5 me-1" />
              {t('Clear All')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
})

// ─── Summary Cards (dynamic from API response) ───
const SUMMARY_CARDS: { key: string; label: string; color: string; icon: string }[] = [
  { key: 'totalBookings', label: 'Total Bookings', color: 'primary', icon: 'chart-simple' },
  { key: 'confirmed', label: 'Confirmed', color: 'success', icon: 'check-circle' },
  { key: 'completed', label: 'Completed', color: 'info', icon: 'like' },
  { key: 'pending', label: 'Pending', color: 'warning', icon: 'time' },
  { key: 'cancelled', label: 'Cancelled', color: 'danger', icon: 'cross-circle' },
  { key: 'rejected', label: 'Rejected', color: 'dark', icon: 'shield-cross' },
  { key: 'repeatCustomers', label: 'Repeat Customers', color: 'info', icon: 'people' },
  { key: 'totalCustomers', label: 'Total Customers', color: 'primary', icon: 'people' },
]

const SummarySection: FC<{ summary: BookingSummary; t: (key: string) => string }> = React.memo(({ summary, t }) => {
  if (!summary) return null

  const cards = SUMMARY_CARDS
    .filter(card => summary?.[card.key] !== undefined && summary?.[card.key] !== null)
    .map(card => {
      const raw = summary[card.key]
      const isObj = typeof raw === 'object' && raw !== null && 'count' in raw
      const value = isObj ? (raw.count ?? 0) : (Number(raw) || 0)
      const pct = isObj ? raw.percent : null
      return { ...card, value: value.toLocaleString(), subtitle: pct != null && pct > 0 ? `${pct}%` : undefined }
    })

  return (
    <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-4 g-4 mb-6">
      {cards.map(card => (
        <KpiCard key={card.key} title={t(card.label)} value={card.value} subtitle={card.subtitle} color={card.color} icon={card.icon} />
      ))}
    </div>
  )
})

// ─── Top Customers Table ───
const TopCustomersTable: FC<{ data: TopCustomer[]; isLoading: boolean; t: (key: string) => string }> = React.memo(({ data, isLoading, t }) => {
  const columns = useMemo(() => [
    
    {
      name: t('Customer'),
      cell: (row: any) => (
        <div className="py-2">
          <div className="fw-bold text-gray-900 fs-6">{row?.fullName ?? row?.customerName ?? '-'}</div>
          <div className="text-gray-500 fs-8">{row?.email ?? ''}</div>
        </div>
      ),
    },
    {
      name: t('Total Bookings'),
      cell: (row: any) => <span className="fw-bold text-gray-900 fs-6">{row?.totalBookings ?? 0}</span>,
      width: '140px',
    },
    {
      name: t('Total Spent'),
      cell: (row: any) => <span className="fw-bold text-gray-900 fs-6">{Number(row?.totalSpent ?? 0).toLocaleString()} UZS</span>,
      width: '180px',
    },
    {
      name: t('Last Booking'),
      cell: (row: any) => <span className="fw-semibold text-gray-700 fs-7">{row?.lastBooking ?? '-'}</span>,
      width: '140px',
    },
  ], [t])

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Top Customers')}</h3>
      </div>
      <div className="card-body pt-0">
        <DataTable
          columns={columns}
          data={data ?? []}
          progressPending={isLoading}
          progressComponent={
            <div className="py-10 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('Loading...')}</span>
              </div>
            </div>
          }
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 20, 30]}
          highlightOnHover
          noDataComponent={
            <div className="text-center py-10">
              <KTIcon iconName="people" className="fs-2tx text-gray-300 mb-5" />
              <div className="text-gray-500 fs-5">{t('No customers found')}</div>
            </div>
          }
          paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        />
      </div>
    </div>
  )
})

// ─── Main Page ───
const BookingAnalytics: FC = () => {
  const tFn = Tr()
  const tRef = useRef(tFn)
  tRef.current = tFn
  const t = useCallback((key: string) => tRef.current(key), [])
  const categoryList = useCategoryList()

  const [dateRange, setDateRange] = useState<Date[]>([])
  const [selectedCategory, setSelectedCategory] = useState<any>(ALL_CATEGORY_OPTION)

  const { summary, trend, byListing, cancellationReasons, topCustomers, isLoading } = useBookingAnalytics(selectedCategory, dateRange)

  useEffect(() => {
    document.title = 'Insayr | Booking Analytics'
  }, [])

  // Limit listing data to top N for the chart
  const topListings = useMemo(() => {
    const sorted = [...(byListing ?? [])].sort((a: any, b: any) => (b?.confirmed ?? 0) - (a?.confirmed ?? 0))
    return sorted.slice(0, MAX_LISTING_BARS)
  }, [byListing])

  // Limit cancellation reasons — group extras into "Other"
  const processedReasons = useMemo(() => {
    const reasons = [...(cancellationReasons ?? [])]
    if (reasons.length <= MAX_CANCELLATION_ITEMS) return reasons
    const top = reasons.slice(0, MAX_CANCELLATION_ITEMS - 1)
    const rest = reasons.slice(MAX_CANCELLATION_ITEMS - 1)
    const otherCount = rest.reduce((sum: number, r: any) => sum + (r?.count ?? 0), 0)
    return [...top, { reason: 'Other', count: otherCount }]
  }, [cancellationReasons])

  // ─── Chart Options (memoized) ───
  const trendChartOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'area', toolbar: { show: false }, fontFamily: 'inherit' },
    series: [
      { name: t('Confirmed'), data: (trend ?? []).map((item: any) => item?.confirmed ?? 0) },
      { name: t('Cancelled'), data: (trend ?? []).map((item: any) => item?.cancelled ?? 0) },
    ],
    xaxis: { categories: (trend ?? []).map((item: any) => item?.label ?? item?.date ?? '') },
    colors: ['#50cd89', '#f1416c'],
    stroke: { curve: 'smooth', width: 2.5 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'right' },
    grid: { borderColor: '#f1f3f8', strokeDashArray: 4 },
    yaxis: { labels: { formatter: (val: number) => Math.round(val).toString() } },
    tooltip: { theme: 'light', y: { formatter: (val: number) => Math.round(val).toString() } },
  }), [trend])

  const listingChartOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    series: [{ name: t('Confirmed'), data: topListings.map((h: any) => h?.confirmed ?? 0) }],
    xaxis: {
      categories: topListings.map((h: any) => {
        const name = h?.listingName ?? ''
        return name.length > 20 ? name.substring(0, 20) + '...' : name
      }),
    },
    colors: ['#009ef7'],
    plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '55%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f1f3f8', strokeDashArray: 4 },
    tooltip: {
      theme: 'light',
      y: { formatter: (val: number) => `${val} confirmed` },
      x: {
        formatter: (_: any, opts: any) => {
          const idx = opts?.dataPointIndex ?? 0
          return topListings[idx]?.listingName ?? ''
        },
      },
    },
  }), [topListings])

  const cancellationChartOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'donut', fontFamily: 'inherit' },
    series: processedReasons.map((r: any) => r?.count ?? 0),
    labels: processedReasons.map((r: any) => {
      const reason = r?.reason ?? ''
      return reason.length > 25 ? reason.substring(0, 25) + '...' : reason
    }),
    colors: ['#f1416c', '#ffc700', '#7239ea', '#009ef7', '#50cd89', '#181c32', '#a1a5b7', '#3f4254'],
    legend: { position: 'bottom', fontSize: '12px', itemMargin: { horizontal: 8, vertical: 4 } },
    dataLabels: { enabled: true, formatter: (val: number) => `${val?.toFixed?.(0) ?? 0}%` },
    plotOptions: { pie: { donut: { size: '65%' } } },
    tooltip: {
      y: { formatter: (val: number) => `${val} cancellations` },
    },
    responsive: [{ breakpoint: 480, options: { chart: { width: 280 }, legend: { position: 'bottom' } } }],
  }), [processedReasons])

  const handleClear = useCallback(() => {
    setDateRange([])
    setSelectedCategory(ALL_CATEGORY_OPTION)
  }, [])

  const handleDateChange = useCallback((dates: Date[]) => {
    setDateRange(dates?.length === 2 ? dates : [])
  }, [])

  const handleCategoryChange = useCallback((val: any) => {
    setSelectedCategory(val)
  }, [])

  const chartTitle = selectedCategory?.value
    ? `${t('Top')} ${MAX_LISTING_BARS} - ${selectedCategory.label}`
    : `${t('Top')} ${MAX_LISTING_BARS} ${t('Listings')}`

  const hasListings = (topListings?.length ?? 0) > 0 && topListings.some((h: any) => (h?.confirmed ?? 0) > 0)
  const hasReasons = (processedReasons?.length ?? 0) > 0
  const hasTrendData = (trend?.length ?? 0) > 0 && trend.some((item: any) => (item?.confirmed ?? 0) > 0 || (item?.cancelled ?? 0) > 0)

  return (
    <>
      <ToastContainer />

      <FilterBar
        dateRange={dateRange}
        selectedCategory={selectedCategory}
        categoryList={categoryList}
        onDateChange={handleDateChange}
        onCategoryChange={handleCategoryChange}
        onClear={handleClear}
        t={t}
      />

      <SummarySection summary={summary} t={t} />

      <div className="mb-6">
        {hasTrendData ? (
          <ChartCard title={t('Booking Trend')} chartId="booking-trend-chart" options={trendChartOptions} height={320} />
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-header border-0 pt-6 pb-0">
              <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Booking Trend')}</h3>
            </div>
            <div className="card-body text-center py-15">
              <KTIcon iconName="chart-simple" className="fs-3tx text-gray-300 mb-4" />
              <div className="text-gray-500 fs-5 fw-semibold">{t('No data found')}</div>
            </div>
          </div>
        )}
      </div>

      <div className="row g-4 mb-6">
        <div className="col-lg-7">
          {hasListings ? (
            <ChartCard
              title={chartTitle}
              chartId="bookings-by-listing-chart"
              options={listingChartOptions}
              height={Math.min(topListings.length * 40 + 60, 400)}
            />
          ) : (
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header border-0 pt-6 pb-0">
                <h3 className="card-title fw-bold text-gray-900 fs-5">{chartTitle}</h3>
              </div>
              <div className="card-body text-center py-15">
                <KTIcon iconName="chart-simple" className="fs-3tx text-gray-300 mb-4" />
                <div className="text-gray-500 fs-5 fw-semibold">{t('No data found')}</div>
              </div>
            </div>
          )}
        </div>
        <div className="col-lg-5">
          {hasReasons ? (
            <ChartCard title={t('Cancellation Reasons')} chartId="cancellation-reasons-chart" options={cancellationChartOptions} height={350} />
          ) : (
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header border-0 pt-6 pb-0">
                <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Cancellation Reasons')}</h3>
              </div>
              <div className="card-body text-center py-15">
                <KTIcon iconName="chart-simple" className="fs-3tx text-gray-300 mb-4" />
                <div className="text-gray-500 fs-5 fw-semibold">{t('No data found')}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TopCustomersTable data={topCustomers} isLoading={isLoading} t={t} />
    </>
  )
}

export default BookingAnalytics
